
import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faLock,
    faUnlock,
    faMoon,
    faSun,
} from '@fortawesome/free-solid-svg-icons';
import {EventMeta} from '../../types';
import UserDropdown from "@/components/common/UserDropdown.tsx";
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import CompactModeToggle from '../common/CompactModeToggle';

interface EventHeaderProps {
    meta: EventMeta;
    isEventClosed: boolean;
    // The status lives on its own listener, so it can still be in flight while the
    // event itself has rendered. Until it lands the badge shows neither state.
    statusLoading?: boolean;
    darkMode: boolean;
    onToggleDarkMode: () => void;
}

const EventHeader: React.FC<EventHeaderProps> = ({
                                                     meta,
                                                     isEventClosed,
                                                     statusLoading = false,
                                                     darkMode,
                                                     onToggleDarkMode,
                                                 }) => {
    const { user } = useAuth();
    const { t } = useTranslation();

    return (
        <header className="glass-header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Left side - Back & Event Info */}
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                        <Link
                            to={user ? "/my-events" : "/"}
                            className="glass-header-back flex-shrink-0"
                            title={t('event.header.back')}
                        >
                            <FontAwesomeIcon icon={faArrowLeft}/>
                        </Link>

                        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                            <div className="glass-header-icon w-9 h-9 flex-shrink-0 ring-2 ring-white/30 dark:ring-white/10">
                                <img src="/images/logo192.png" className="w-[22px] h-[22px] relative z-10"/>
                            </div>
                            <div className="min-w-0">
                                <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate max-w-[200px] sm:max-w-[300px] lg:max-w-none leading-tight">
                                    {meta.name}
                                </h1>
                                {/* Event Status — below xs the label has no room
                                    left beside the title, so only the icon shows.
                                    The currency unit used to sit alongside it; it
                                    now rides under the đ|K switch in the action row,
                                    where the control it describes actually is. */}
                                <div className="flex items-center gap-1.5 min-w-0">
                                    {statusLoading ? (
                                        <div
                                            role="status"
                                            aria-label={t('event.header.status.active')}
                                            className="h-4 w-16 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"
                                        />
                                    ) : (
                                        <div className={`glass-status-badge text-[10px] sm:text-xs whitespace-nowrap ${
                                            isEventClosed ? 'closed' : 'active'
                                        }`}>
                                            <FontAwesomeIcon icon={isEventClosed ? faLock : faUnlock} className="text-[9px] xs:mr-1" />
                                            <span className="sr-only xs:not-sr-only">
                                                {isEventClosed ? t('event.header.status.closed') : t('event.header.status.active')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right side - Actions */}
                    <div className="flex items-center space-x-2 flex-shrink-0">
                        <CompactModeToggle />
                        {/* Duplicated by the dark-mode item inside UserDropdown,
                            so it gives up its space on narrow screens */}
                        <button
                            onClick={onToggleDarkMode}
                            className="glass-header-back !p-2.5 hidden sm:block"
                            title={darkMode ? 'Light mode' : 'Dark mode'}
                        >
                            <FontAwesomeIcon icon={darkMode ? faSun : faMoon} className="text-sm" />
                        </button>
                        <UserDropdown darkMode={darkMode} onToggleDarkMode={onToggleDarkMode} />
                    </div>
                </div>
            </div>
        </header>
    );
};

export default EventHeader;